import { useEffect, useState } from 'react';

import { ReportSettingsErrorType, ReportSettingsType } from './ReportSettingsFragment.props';
import {
  ActionPermissions,
  CustomPermission,
  NodeType,
  StructureResponse,
} from '../../../../../@types';
import { DropdownItem } from '../../../../../components/atoms/SelectMenu';
import { StructureTypeCategory } from '../../../../../configs/enums';
import { isUserHasPermission } from '../../../../../configs/permissions';
import {
  REPORT_SETTINGS_DEFAULT_DATA,
  REPORT_SETTINGS_DEFAULT_ERROR_DATA,
} from '../../../../../constants/report';
import { useGetReportByCode, useUpdateReportByCode } from '../../../../../queries/report-query';
import {
  useGetNodeChildrenByNodeType,
  useGetStructuresByTypeCategoryCodeAndTypeCode,
} from '../../../../../queries/structure-query';
import { toast } from '../../../../../utils/toast';

/** Structures arrive as full nodes; the dropdowns only want value/label/disabled. */
const toDropdownItems = (nodes: StructureResponse[]): DropdownItem[] =>
  nodes.map((item) => ({ value: item.id.toString(), label: item.name, disabled: !item.isActive }));

/**
 * State, queries and the client → contract → brand cascade for the Report Settings
 * tab, so the component itself is only chrome — the same split as
 * `useScheduleEmailForm` next door.
 *
 * The cascade is deliberately sequential: selecting a client refetches its contracts,
 * which refetches their brands, each step re-pointing the one `useGetNodeChildrenByNodeType`
 * query. That is the existing behaviour and is unchanged here.
 */
export const useReportSettingsForm = (reportCode: string, permissions: CustomPermission[]) => {
  const [reportSettings, setReportSettings] = useState<ReportSettingsType>(
    REPORT_SETTINGS_DEFAULT_DATA
  );
  const [errors, setErrors] = useState<ReportSettingsErrorType>(REPORT_SETTINGS_DEFAULT_ERROR_DATA);
  const [showObfuscateWarning, setShowObfuscateWarning] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState('');
  const [queryNodeType, setQueryNodeType] = useState(NodeType.Client);
  const [clients, setClients] = useState<DropdownItem[]>([]);
  const [contracts, setContracts] = useState<DropdownItem[]>([]);
  const [brands, setBrands] = useState<DropdownItem[]>([]);

  const getReportByCode = useGetReportByCode(reportCode);
  const updateReportByCode = useUpdateReportByCode(reportCode);
  const getStructures = useGetStructuresByTypeCategoryCodeAndTypeCode(
    StructureTypeCategory.System,
    NodeType.Client
  );
  const getNodeChildren = useGetNodeChildrenByNodeType(selectedParentId, queryNodeType);

  useEffect(() => {
    getReportByCode.refetch();
    // Mount-only kick-off for an `enabled: false` query; the query object is a new
    // identity every render and listing it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!getReportByCode.data) return;
    setReportSettings(getReportByCode.data.reportProperties || REPORT_SETTINGS_DEFAULT_DATA);
    if (getReportByCode.data.reportProperties?.obfuscation) {
      // Seed the cascade at the saved client so the picker reopens where it left off.
      // `?? ''` throughout: `ReportSettingsType` types these as required, but the
      // saved `reportProperties` blob is whatever the API stored, so a record from
      // before obfuscation existed arrives with the arrays missing.
      setSelectedParentId(
        getReportByCode.data.reportProperties.obfuscation.selectedClients?.[0] ?? ''
      );
      setQueryNodeType(NodeType.Contract);
      // No `refetch()` here: `getNodeChildren` is still bound to the *previous*
      // parent id and node type — the two setters above have not been applied yet —
      // so it would fetch the wrong children. The effect below reacts to those two
      // values once React has committed them and fetches the right ones.
    }
  }, [getReportByCode.data]);

  useEffect(() => {
    if (getStructures.data) setClients(toDropdownItems(getStructures.data));
  }, [getStructures.data]);

  useEffect(() => {
    // 'any' and multi-select parents have no single child set to fetch.
    if (
      selectedParentId &&
      queryNodeType &&
      selectedParentId !== 'any' &&
      selectedParentId.split(',').length === 1
    ) {
      getNodeChildren.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParentId, queryNodeType]);

  useEffect(() => {
    if (!getNodeChildren.data) return;
    if (queryNodeType === NodeType.Contract) {
      setContracts(toDropdownItems(getNodeChildren.data));
      setSelectedParentId(reportSettings.obfuscation?.selectedContracts?.[0] ?? '');
      setQueryNodeType(NodeType.Brand);
    } else if (queryNodeType === NodeType.Brand) {
      setBrands(toDropdownItems(getNodeChildren.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNodeChildren.data]);

  const setObfuscation = (patch: Partial<ReportSettingsType['obfuscation']>) =>
    setReportSettings((current) => ({
      ...current,
      obfuscation: { ...current.obfuscation, ...patch },
    }));

  return {
    reportSettings,
    errors,
    canObfuscate: isUserHasPermission(
      ActionPermissions.Report_My_Reports_Report_Settings_Can_Obfuscate,
      permissions
    ),
    clients,
    contracts,
    brands,
    isPickerOpen,
    showObfuscateWarning,
    isSaving: updateReportByCode.isPending,
    /** Turning it on needs confirming; turning it off is immediate. */
    onToggleObfuscate: (checked: boolean) => {
      setObfuscation({ obfuscate: checked });
      if (checked) setShowObfuscateWarning(true);
    },
    onConfirmObfuscate: () => {
      setObfuscation({ obfuscate: true });
      setShowObfuscateWarning(false);
    },
    onCancelObfuscate: () => {
      setObfuscation({ obfuscate: false });
      setShowObfuscateWarning(false);
    },
    onOpenPicker: () => {
      setIsPickerOpen(true);
      getStructures.refetch();
    },
    onClosePicker: () => setIsPickerOpen(false),
    onChangeNodeType: (nodeType: NodeType, parentId: string) => {
      setSelectedParentId(parentId);
      setQueryNodeType(nodeType);
    },
    onSelectHierarchy: (
      parentId: string,
      hierarchy: string,
      clientId?: string,
      contractId?: string
    ) => {
      setIsPickerOpen(false);
      if (hierarchy) setErrors({ hasNoClientHierarchy: false });
      setObfuscation({
        clientHierarchy: hierarchy,
        selectedClients: [clientId as string],
        selectedContracts: [contractId as string],
        selectedBrands: [parentId],
      });
    },
    handleSave: async () => {
      if (reportSettings.obfuscation?.clientHierarchy === '') {
        setErrors({ hasNoClientHierarchy: true });
        return;
      }
      try {
        await updateReportByCode.mutateAsync({
          ...getReportByCode.data,
          reportProperties: reportSettings,
        });
      } catch {
        // The footer calls this straight from `onClick`, so a rejection here used
        // to go nowhere while the success toast never fired — leaving the tab
        // silent and the user unsure whether anything happened.
        toast.error('Settings not saved', {
          description: 'Your changes were not saved. Please try again.',
        });
        return;
      }
      getReportByCode.refetch();
      toast.success('Settings saved');
    },
  };
};
